import React from 'react';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ChevronLeft, ChevronRight, Plus } from 'lucide-react';
import { format, getWeek, startOfMonth, endOfMonth } from 'date-fns';
import { cn } from '@/lib/utils';

/**
 * CalendarHeader - Header section with date badge, navigation, view toggle, and add button
 *
 * @param {Object} props
 * @param {Date} props.currentDate - Currently displayed date
 * @param {string} props.view - Current view ('month' | 'week' | 'day')
 * @param {function} props.onPrevious - Navigate to previous period
 * @param {function} props.onNext - Navigate to next period
 * @param {function} props.onToday - Navigate to today
 * @param {function} props.onViewChange - Change view mode
 * @param {function} props.onAddEvent - Add event button click
 * @param {boolean} props.showAddButton - Whether to show add event button
 * @param {React.ReactNode} props.actions - Additional action buttons
 * @param {React.ReactNode} props.filterLegend - Filter legend to show next to navigation arrows
 * @param {string} props.className - Additional classes
 */
export function CalendarHeader({
  currentDate,
  view = 'month',
  onPrevious,
  onNext,
  onToday,
  onViewChange,
  onAddEvent,
  showAddButton = true,
  actions,
  filterLegend,
  className
}) {
  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(currentDate);
  const weekNumber = getWeek(currentDate);

  const viewOptions = [
    { value: 'month', label: 'Month' },
    { value: 'week', label: 'Week' }
  ];

  return (
    <div className={cn('flex items-center justify-between py-4 px-1', className)}>
      {/* Left side - Date badge and title */}
      <div className="flex items-center gap-4">
        {/* Date Badge */}
        <div className="flex flex-col items-center justify-center w-12 h-14 bg-rose-50 rounded-lg border border-rose-100">
          <span className="text-[10px] font-semibold text-rose-600 uppercase tracking-wide">
            {format(currentDate, 'MMM')}
          </span>
          <span className="text-xl font-bold text-rose-700 -mt-0.5">
            {format(currentDate, 'd')}
          </span>
        </div>

        {/* Month/Year Title */}
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-xl font-semibold text-gray-900">
              {format(currentDate, 'MMMM yyyy')}
            </h2>
            <span className="text-sm text-gray-500">
              Week {weekNumber}
            </span>
          </div>
          <p className="text-sm text-gray-500">
            {format(monthStart, 'MMM d, yyyy')} - {format(monthEnd, 'MMM d, yyyy')}
          </p>
        </div>
      </div>

      {/* Right side - Navigation and actions */}
      <div className="flex items-center gap-2">
        {/* Filter legend - first in the row */}
        {filterLegend}

        {/* Navigation arrows */}
        <div className="flex items-center border rounded-lg">
          <Button
            variant="ghost"
            size="icon"
            className="h-9 w-9 rounded-r-none border-r"
            onClick={onPrevious}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-9 w-9 rounded-l-none"
            onClick={onNext}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>

        {/* Today button */}
        <Button
          variant="outline"
          size="sm"
          onClick={onToday}
          className="h-9"
        >
          Today
        </Button>

        {/* View selector */}
        {onViewChange && (
          <Select value={view} onValueChange={onViewChange}>
            <SelectTrigger className="w-[130px] h-9">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {viewOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        {/* Custom actions */}
        {actions}

        {/* Add event button */}
        {showAddButton && onAddEvent && (
          <Button onClick={onAddEvent} size="sm" className="h-9">
            <Plus className="h-4 w-4 mr-1" />
            Add event
          </Button>
        )}
      </div>
    </div>
  );
}

export default CalendarHeader;
